import { isTauri } from '@/tauri/utils';
import { getClientVersion, getDesktopBuildId } from '@/utils/appVersion';
import { type BuildProfile, BUILD_PROFILE } from '@/utils/buildProfile';
import { formatVersion } from '@/utils/releaseVersion';

export type HeidiBuildInfo = {
  /** Client version string — desktop semver or web release tag. */
  version: string;
  /** Short git SHA baked in at build time. */
  sha: string;
  /** Deploy environment (`NEXT_PUBLIC_ENVIRONMENT`). */
  environment: string;
  platform: 'web' | 'desktop';
  buildProfile: BuildProfile;
  /** CI desktop build id — absent on web and local desktop builds. */
  buildId?: string;
  /** Vercel deployment id — absent on desktop/local builds. */
  deploymentId?: string;
  /** Raw git ref from the build platform. */
  releaseRef?: string;
};

let buildBannerInitialized = false;

const shortSha = (sha: string | undefined): string =>
  (sha && sha.length > 0 ? sha : 'local').slice(0, 7);

export const collectHeidiBuildInfo = (): HeidiBuildInfo => {
  const version =
    getClientVersion() ??
    formatVersion(
      process.env.NEXT_PUBLIC_RELEASE_REF,
      process.env.NEXT_PUBLIC_RELEASE_SHA
    );

  const buildId = getDesktopBuildId();
  const deploymentId = process.env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID;
  const releaseRef = process.env.NEXT_PUBLIC_RELEASE_REF;

  return {
    version,
    sha: shortSha(process.env.NEXT_PUBLIC_RELEASE_SHA),
    environment: process.env.NEXT_PUBLIC_ENVIRONMENT ?? 'unknown',
    platform: isTauri() ? 'desktop' : 'web',
    buildProfile: BUILD_PROFILE,
    ...(buildId ? { buildId } : {}),
    ...(deploymentId ? { deploymentId } : {}),
    ...(releaseRef ? { releaseRef } : {}),
  };
};

const BADGE_STYLES = [
  'padding: 0.15em 0.45em; border-radius: 0.35em; background: #3b2f6b; color: #fff;',
  'padding: 0.15em 0.45em; border-radius: 0.35em; background: #1f6feb; color: #fff;',
  'padding: 0.15em 0.45em; border-radius: 0.35em; background: #57606a; color: #fff;',
  'padding: 0.15em 0.45em; border-radius: 0.35em; background: #8250df; color: #fff;',
  'padding: 0.15em 0.45em; border-radius: 0.35em; background: #116329; color: #fff;',
] as const;

const logStyledBuildBanner = (info: HeidiBuildInfo): void => {
  const tags = [
    info.environment,
    info.platform,
    `commit ${info.sha}`,
    ...(info.buildId ? [`build ${info.buildId}`] : []),
    info.version,
  ];

  const formatParts = ['%c Heidi %c —'];
  const styles: string[] = ['font-weight: 700;', ''];

  tags.forEach((tag, index) => {
    if (index > 0) {
      formatParts.push('%c ·');
      styles.push('');
    }
    formatParts.push(`%c ${tag}`);
    styles.push(BADGE_STYLES[index % BADGE_STYLES.length] ?? BADGE_STYLES[0]);
  });

  // eslint-disable-next-line no-console -- intentional boot banner for support/debug
  console.info(formatParts.join(' '), ...styles);
};

declare global {
  interface Window {
    /** Build metadata for DevTools inspection — `copy(__HEIDI__)` in support chats. */
    __HEIDI__?: HeidiBuildInfo;
  }
}

/** Logs a PostHog-style boot banner and exposes build metadata on `window.__HEIDI__`. */
export const initBuildBanner = (): void => {
  if (typeof window === 'undefined' || buildBannerInitialized) {
    return;
  }

  buildBannerInitialized = true;

  const info = collectHeidiBuildInfo();
  window.__HEIDI__ = info;
  logStyledBuildBanner(info);
};
